import React from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { useAuth } from "@/config/auth";

/**
 * AdminGuard — small banner shown above editable rubric/benchmark pages.
 * - If admin: shows a subtle "Admin Edit Mode" tag.
 * - If not admin: shows a read-only notice that explains why edits are disabled.
 * Backend already enforces 403 on PUT endpoints; this is UX hygiene only.
 */
export default function AdminGuard({ resource = "benchmarks" }) {
  const { user, isAdmin } = useAuth();
  if (isAdmin) {
    return (
      <div className="mb-4 px-3 py-2 rounded-md bg-[#1B291D]/5 border border-[#1B291D]/15 flex items-center gap-2" data-testid="admin-guard-admin">
        <ShieldCheck className="w-4 h-4 text-[#1B291D]" />
        <p className="text-[11px] text-[#1B291D] tracking-wider uppercase font-bold">Admin Edit Mode</p>
        <p className="text-[11px] text-[#617364]">— You can edit {resource}. Changes are saved server-side.</p>
      </div>
    );
  }
  return (
    <div className="mb-4 px-3 py-2 rounded-md bg-[#F57F17]/5 border border-[#F57F17]/20 flex items-center gap-2" data-testid="admin-guard-readonly">
      <ShieldAlert className="w-4 h-4 text-[#F57F17]" />
      <p className="text-[11px] text-[#F57F17] tracking-wider uppercase font-bold">Read-only</p>
      <p className="text-[11px] text-[#617364]">
        {user ? `Your account does not have admin permissions — editing ${resource} is disabled.`
              : `Sign in with an admin account to edit ${resource}.`}
      </p>
    </div>
  );
}

export function useCanEdit() {
  const { isAdmin } = useAuth();
  return isAdmin;
}
